//@ts-nocheck

'use client'

import React, { useContext, useEffect, useId, useRef, useState } from 'react'

import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Scrollbar, A11y } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'

import { CtaTypes } from '../../../types/Cta'

import { SectionThemeContext } from '../../../contexts/SectionTheme.context'

import useSwiperRef from '../../../hooks/useSwiperRef'
import useMediaQuery from '../../../hooks/useMediaQuery'

import Button, { ButtonForwardingRef } from '../../atoms/Button/Button.component'
import { Paragraph } from '../../atoms/Typography/Typography.component'
import { Col, Container, Row, SpacerBase } from '../../atoms/GridSystem'
import Icon from '../../atoms/Icon/Icon.component'

import CardUseCases from '../../molecules/CardUseCases/CardUseCases.component'
import CardExpertise from '../../molecules/CardExpertise/CardExpertise.component'
import CardSquare from '../../molecules/CardSquare/CardSquare.component'
import CardNews from '../../molecules/CardNews/CardNews.component'
import CardDocuments from '../../molecules/CardDocuments/CardDocuments.component'
import CardCertifications from '../../molecules/CardCertifications/CardCertifications.component'

import * as S from './CarouselCards.styles'

type CarouselCardTypes = | 'RelatedUseCases' | 'RelatedExpertise' | 'RelatedSquare' | 'RelatedResources' | 'RelatedNews' | 'RelatedCertifications'

interface CarouselCardsProps {
  type: string
  sectionId: string
  as?: React.ElementType
  title?: string
  body?: string
  cta?: CtaTypes
  ctaPrimary?: CtaTypes
  items: any[]
  inset?: boolean
  isComparison?: boolean
}

const CarouselCards = ({
  type,
  sectionId,
  as = 'section',
  preTitle,
  title,
  body,
  cta,
  ctaPrimary,
  items,
  inset = false,
  isComparison = false,
  withBackground,
  ...props
}: CarouselCardsProps) => {
  const [isMobile, setIsMobile] = useState(useMediaQuery('(max-width: 1023px)'))
  const [isMobileGroupedItems, setIsMobileGroupedItems] = useState(useMediaQuery('(max-width: 1279px)'))
  const componentId = useId();
  const swiperRef = useRef<SwiperType | null>(null)
  const isItemsMoreThanThree = items && items.length > 3
  const { selectedColorScheme } = useContext(SectionThemeContext)
  const [nextEl, nextElRef] = useSwiperRef()
  const [prevEl, prevElRef] = useSwiperRef()
  const [scrollbarEl, scrollbarElRef] = useSwiperRef()
  const [hideScrollbar, setHideScrollbar] = useState(false)

  const dispatcher = {
    CardCase: CardUseCases,
    CardExpertise: CardExpertise,
    CardSquare: CardSquare,
    CardDocuments: CardDocuments,
    CardNews: CardNews,
    CardCertification: CardCertifications
  }

  const isVariantGrouped = type === 'CardNews' || type === 'CardDocuments'

  const getSwiperConfigByCardType = (type: CarouselCardTypes) => {
    switch (type) {
      case 'CardSquare':
      case 'CardCase':
        return {
          375: {
            slidesPerView: 1.5,
          },
          768: {
            slidesPerView: 1.2,
          },
          992: {
            slidesPerView: 3,
          },
          1280: {
            slidesPerView: 3,
            spaceBetween: 48,
          },
        }
      case 'CardNews':
        return {
          375: {
            slidesPerView: 1.5,
          },
          768: {
            slidesPerView: 1.2,
          },
          992: {
            slidesPerView: 3,
          },
          1280: {
            slidesPerView: 1,
          },
        }
      case 'CardExpertise':
        return {
          375: {
            slidesPerView: 1,
          },
          768: {
            slidesPerView: 3,
          },
          992: {
            slidesPerView: 3,
          },
          1280: {
            slidesPerView: 4,
            spaceBetween: 48,
          },
        }
      case 'CardCertification':
        return {
          375: {
            slidesPerView: 1,
          },
          768: {
            slidesPerView: 1.5,
          },
          992: {
            slidesPerView: 3,
          },
          1280: {
            slidesPerView: 4,
            spaceBetween: 48,
          },
        }
      case 'CardDocuments':
        return {
          375: {
            slidesPerView: 1,
          },
          768: {
            slidesPerView: 1.2,
          },
          992: {
            slidesPerView: 2,
          },
          1280: {
            slidesPerView: 1,
            spaceBetween: 0,
          },
        }
    }
  }

  const Component = dispatcher[type]

  const renderDesktopNewsResources = () => {
    const groupBySix = (items: any[]): any[][] => {
      return items.reduce((resultArray, item, index) => {
        const groupIndex = Math.floor(index / 6);
        if (!resultArray[groupIndex]) {
          resultArray[groupIndex] = [];
        }
        resultArray[groupIndex].push(item);
        return resultArray;
      }, []);
    };

    const groupedItems = groupBySix(items);

    return groupedItems.map((group, index) => (
      <SwiperSlide key={`CarouselCards-Item-${index}-${group[0]?.id}`}>
        <Container>
          <Row>
            {group.map((item, i) => (
              <Col
                xs={12}
                xl={4}
                key={`CarouselCards-Item-${i}-${item?.id}`}
              >
                <Component
                  data={item}
                  {...item}
                  componentId={componentId}
                  parentColorScheme={selectedColorScheme}
                  withBackground={withBackground}
                />
                <SpacerBase xs={8} />
              </Col>
            ))}
          </Row>
        </Container>
      </SwiperSlide>
    ));
  }

  const updateSwiperPadding = (swiper: SwiperType) => {
    return
    const swiperWrapper = swiper.el.querySelector('.swiper-wrapper')
    if (swiperWrapper) {
      if (swiper.isBeginning || swiper.isEnd) {
        if (swiper.isBeginning) {
          (swiperWrapper as HTMLElement).style.marginLeft = '-50px'
        } else {
          (swiperWrapper as HTMLElement).style.marginLeft = '50px'
        }
      } else {
        (swiperWrapper as HTMLElement).style.marginLeft = '0px'
      }
    }
  }

  useEffect(() => {
    const swiper = swiperRef.current
    if (swiper) {
      swiper.on('slideChange', () => updateSwiperPadding(swiper))
      swiper.on('init', () => updateSwiperPadding(swiper))
      updateSwiperPadding(swiper)
    }

    setHideScrollbar(swiper.isBeginning && swiper.isEnd)
  }, [])

  return (
    <S.CarouselCards
      colorScheme={selectedColorScheme}
      withBackground={withBackground}
      as={as}
      id={sectionId}
      inset={inset}
      type={type}
    >
      <Container>
        {cta || title || body || preTitle ? (
          <S.CarouselCardsTexts colorScheme={selectedColorScheme} withBackground={withBackground}>
            { preTitle ? (
              <Row>
                <Col xs={12} $align={'flex-start'}>
                  <Icon name="leaf-green" useIconColor unlockedProportions size={'regular'} />
                  <SpacerBase xs={1.5} />
                </Col>
                <Col xs={12}>
                  <Paragraph
                    typo="paragraphMF"
                    {...preTitle}
                  />
                  <SpacerBase xs={1.5} />
                </Col>
              </Row>
            ) : null}
            {title || cta ?(
              <Row>
                <Col
                  xs={12}
                  lg={6}
                  $direction="column"
                  $align="flex-start"
                  noGutter
                >
                  {title ? (
                    <Paragraph
                      typo="headingXL"
                      weight="medium"
                      {...title}
                    />
                  ) : null}
                </Col>
                <Col xs={12} lg={6} noGutter $align={{ xs: 'flex-start', lg: 'flex-end' }}>
                  {cta ? (
                    <>
                      <SpacerBase xs={2} lg={0} />
                      <Button
                        {...cta}
                        variant="primary"
                        iconEnd="arrow-right"
                      />
                    </>
                  ) : null}
                </Col>
              </Row>
            ) : null}
            <SpacerBase xs={5} />
          </S.CarouselCardsTexts>
        ) : null}
        <S.SwiperWrapper type={type}>
          <Swiper
            ref={swiperRef}
            onInit={(swiper) => {
              swiperRef.current = swiper
            }}
            modules={[Navigation, Scrollbar, A11y]}
            spaceBetween={24}
            slidesPerView={1.5}
            centerInsufficientSlides={true}
            breakpoints={getSwiperConfigByCardType(type)}
            autoHeight={true}
            navigation={{
              prevEl,
              nextEl,
            }}
            scrollbar={{ el: scrollbarEl }}
            a11y={{
              prevSlideMessage: 'Precedente slide',
              nextSlideMessage: 'Prossima slide',
            }}
            grabCursor={true}
          >
            { !isMobileGroupedItems && isVariantGrouped ? renderDesktopNewsResources() : 
              items.map((item, i) => (
                <SwiperSlide key={`CarouselCards-Item-${i}-${item?.id}`}>
                  <Component
                    data={item}
                    {...item}
                    componentId={componentId}
                    parentColorScheme={selectedColorScheme}
                    withBackground={withBackground}
                  />
                </SwiperSlide>
              ))
            }
          </Swiper>
          {hideScrollbar ? null : (
            <S.Navigation>
              <S.ButtonPrev>
                <ButtonForwardingRef
                  ref={prevElRef}
                  variant="primary"
                  iconEnd={'chevron-left'}
                  iconEndSize="big"
                  inheritedColorScheme={selectedColorScheme}
                  round
                  disabled={swiperRef.current?.isBeginning}
                />
              </S.ButtonPrev>
              <S.ButtonNext>
                <ButtonForwardingRef
                  ref={nextElRef}
                  variant="primary"
                  iconEnd={'chevron-right'}
                  iconEndSize="big"
                  inheritedColorScheme={selectedColorScheme}
                  round
                  disabled={swiperRef.current?.isEnd}
                />
              </S.ButtonNext>
            </S.Navigation>
          )}
        </S.SwiperWrapper>
        {hideScrollbar ? null : (
          <>
            <SpacerBase xs={5} />
            <S.NavScrollbar ref={scrollbarElRef} />
          </>
        )}
        {ctaPrimary ? (
          <S.CarouselCardsButton isItemsMoreThanThree={isItemsMoreThanThree}>
            <Button {...ctaPrimary} variant="primary" iconEnd="arrow-right" />
          </S.CarouselCardsButton>
        ) : null}
      </Container>
    </S.CarouselCards>
  )
}

export default CarouselCards
